import { type ReactNode, useEffect, useRef, useState } from 'react';

import styles from './LazyCategoryRow.module.scss';

interface LazyCategoryRowProps {
  children: ReactNode;
  eager?: boolean;
}

export function LazyCategoryRow({ children, eager = false }: LazyCategoryRowProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(eager);

  // `eager` can flip to true after the placeholder has already mounted (e.g. a
  // scroll-to-game forcing this row), so mount immediately when it does.
  useEffect(() => {
    if (eager) setShouldRender(true);
  }, [eager]);

  useEffect(() => {
    if (shouldRender) return;
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: '1000px 0px' },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [shouldRender]);

  if (shouldRender) {
    return children;
  }

  return <div ref={wrapperRef} className={styles.placeholder} />;
}
